(() => {
  const selectors = {
    addButton: 'product-form [data-add-to-cart]',
    card: '[data-upsell-card]',
    checkbox: '[data-upsell-checkbox]',
    comparePriceText: '[data-compare-text]',
    comparePriceTotal: '[data-compare-price]',
    comparePrice: '[data-upsell-compare-price]',
    errorContainer: '[data-cart-errors-container]',
    image: '[data-upsell-image-holder] img',
    price: '[data-upsell-price]',
    productForm: '[data-product-form]',
    productJson: '[data-product-json]',
    productPrice: '[data-product-price]',
    priceWrapper: '[data-price-wrapper]',
    variantSelect: '[data-upsell-variant-select]',
  };

  class ProductMetafieldUpsell extends HTMLElement {
    connectedCallback() {
      this.productComponent = this.closest('product-component');
      this.form = this.productComponent?.querySelector(selectors.productForm);
      this.addButton = this.productComponent?.querySelector(selectors.addButton);
      this.cards = [...this.querySelectorAll(selectors.card)];
      this.trackingEnabled = this.dataset.upsellTracking === 'true';
      this.productData = this.getProductData();
      this.handleAdd = this.handleAdd.bind(this);
      this.handleMainVariantChange = this.handleMainVariantChange.bind(this);

      this.cards.forEach((card) => this.bindCard(card));
      this.addButton?.addEventListener('click', this.handleAdd, true);
      this.productComponent?.addEventListener('theme:variant:change', this.handleMainVariantChange);
      this.setMainVariantPrices(this.getCurrentMainVariant());
    }

    disconnectedCallback() {
      this.addButton?.removeEventListener('click', this.handleAdd, true);
      this.productComponent?.removeEventListener('theme:variant:change', this.handleMainVariantChange);
    }

    bindCard(card) {
      const checkbox = card.querySelector(selectors.checkbox);
      const select = card.querySelector(selectors.variantSelect);

      checkbox?.addEventListener('change', () => {
        card.classList.toggle('is-selected', checkbox.checked);
        this.updateTotalPrice();
      });

      card.addEventListener('click', (event) => {
        if (event.target.closest('select, input, label')) return;
        checkbox?.click();
      });

      select?.addEventListener('change', () => this.updateVariant(card, select));
    }

    updateVariant(card, select) {
      const option = select.selectedOptions[0];
      const price = card.querySelector(selectors.price);
      const comparePrice = card.querySelector(selectors.comparePrice);
      const image = card.querySelector(selectors.image);

      card.dataset.variantId = option.value;
      card.dataset.upsellVariantSku = option.dataset.sku || '';
      card.dataset.priceCents = option.dataset.priceCents || '0';
      card.dataset.comparePriceCents = option.dataset.comparePriceCents || '';
      price.textContent = option.dataset.price || '';
      comparePrice.textContent = option.dataset.comparePrice || '';
      comparePrice.hidden = !option.dataset.comparePrice;

      if (image && option.dataset.imageUrl) {
        image.src = option.dataset.imageUrl;
        image.srcset = option.dataset.imageSrcset || '';
      }

      this.updateTotalPrice();
    }

    selectedCards() {
      if (!this.productComponent) return [];

      return [
        ...this.productComponent.querySelectorAll(
          `product-metafield-upsell ${selectors.card}`
        ),
      ].filter(
        (card) =>
          card.closest('product-component') === this.productComponent &&
          card.querySelector(selectors.checkbox)?.checked
      );
    }

    handleAdd(event) {
      const selectedCards = this.selectedCards();
      if (!selectedCards.length || !this.form || !this.addButton) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.addButton.disabled || !this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      const items = this.buildItems(selectedCards);
      if (!items.length) return;

      if (window.a11y) window.a11y.lastElement = this.addButton;
      this.submitItems(items);
    }

    buildItems(selectedCards) {
      const formData = new FormData(this.form);
      const mainVariantId = formData.get('id');
      const mainQuantity = Number.parseInt(formData.get('quantity') || '1', 10);

      if (!mainVariantId) return [];

      const mainItem = {
        id: mainVariantId,
        quantity: Number.isNaN(mainQuantity) ? 1 : mainQuantity,
      };
      const sellingPlan = formData.get('selling_plan');
      const properties = {};
      const tracking = this.trackingEnabled
        ? this.buildTrackingData(selectedCards, mainVariantId)
        : null;

      if (sellingPlan) mainItem.selling_plan = sellingPlan;

      for (const [name, value] of formData.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }

      if (tracking) Object.assign(properties, tracking.mainProperties);
      if (Object.keys(properties).length) mainItem.properties = properties;

      return [
        mainItem,
        ...selectedCards.map((card, index) => {
          const item = {
            id: card.dataset.variantId,
            quantity: 1,
          };

          if (tracking) item.properties = tracking.upsellProperties[index];
          return item;
        }),
      ];
    }

    getProductData() {
      const productJson = this.productComponent?.querySelector(selectors.productJson);
      if (!productJson) return null;

      try {
        return JSON.parse(productJson.textContent);
      } catch (error) {
        return null;
      }
    }

    getCurrentMainVariant() {
      const variantId = new FormData(this.form).get('id');
      return this.productData?.variants?.find(
        (variant) => String(variant.id) === String(variantId)
      );
    }

    handleMainVariantChange(event) {
      this.setMainVariantPrices(event.detail?.variant);
      this.updateTotalPrice();
    }

    setMainVariantPrices(variant) {
      if (!variant) return;
      this.mainPriceCents = Number(variant.price) || 0;
      this.mainComparePriceCents = Number(variant.compare_at_price) || 0;
    }

    updateTotalPrice() {
      if (!Number.isFinite(this.mainPriceCents)) return;

      const selectedCards = this.selectedCards();
      let totalPrice = this.mainPriceCents;
      let totalComparePrice =
        this.mainComparePriceCents > this.mainPriceCents
          ? this.mainComparePriceCents
          : this.mainPriceCents;
      let hasComparePrice = this.mainComparePriceCents > this.mainPriceCents;

      selectedCards.forEach((card) => {
        const price = Number(card.dataset.priceCents) || 0;
        const comparePrice = Number(card.dataset.comparePriceCents) || 0;
        totalPrice += price;

        if (comparePrice > price) {
          totalComparePrice += comparePrice;
          hasComparePrice = true;
        } else {
          totalComparePrice += price;
        }
      });

      this.productComponent?.querySelectorAll(selectors.priceWrapper).forEach((wrapper) => {
        const price = wrapper.querySelector(selectors.productPrice);
        const comparePrice = wrapper.querySelector(selectors.comparePriceTotal);
        const compareText = wrapper.querySelector(selectors.comparePriceText);

        if (price) {
          price.textContent =
            totalPrice === 0
              ? window.theme.strings.free
              : window.theme.formatMoney(totalPrice, window.theme.moneyFormat);
          price.classList.toggle('product__price--sale', hasComparePrice);
        }

        if (comparePrice) {
          comparePrice.textContent = hasComparePrice
            ? window.theme.formatMoney(totalComparePrice, window.theme.moneyFormat)
            : '';
          comparePrice.classList.toggle('hidden', !hasComparePrice);
        }

        compareText?.classList.toggle('hidden', !hasComparePrice);
      });
    }

    buildTrackingData(selectedCards, mainVariantId) {
      const bundleId = this.createBundleId();
      const mainHandle = this.productData?.handle || this.dataset.mainProductHandle || '';
      const mainVariant = this.productData?.variants?.find(
        (variant) => String(variant.id) === String(mainVariantId)
      );
      const mainSku = mainVariant?.sku || '';
      const mainProperties = {
        '_bundle-id': bundleId,
        '_bundle-role': 'parent',
        '_bundle-source': 'product-upsell-grid',
        '_product-handle': mainHandle,
        '_product-variant-id': String(mainVariantId),
        '_upsell-count': String(selectedCards.length),
      };

      if (mainSku) mainProperties['_product-sku'] = mainSku;

      const upsellProperties = selectedCards.map((card, index) => {
        const number = index + 1;
        const position = card.dataset.upsellPosition || String(number);
        const handle = card.dataset.productHandle || '';
        const variantId = card.dataset.variantId || '';
        const sku = card.dataset.upsellVariantSku || '';

        mainProperties[`_upsell-${number}-selected`] = handle;
        mainProperties[`_position-of-upsell-${number}`] = position;
        mainProperties[`_upsell-${number}-variant-id`] = variantId;
        if (sku) mainProperties[`_upsell-${number}-sku`] = sku;

        const properties = {
          '_bundle-id': bundleId,
          '_bundle-role': 'upsell',
          '_bundle-source': 'product-upsell-grid',
          '_bundle-parent-handle': mainHandle,
          '_bundle-parent-variant-id': String(mainVariantId),
          '_product-handle': handle,
          '_product-variant-id': variantId,
          '_upsell-index': String(number),
          '_upsell-position': position,
        };

        if (mainSku) properties['_bundle-parent-sku'] = mainSku;
        if (sku) properties['_product-sku'] = sku;
        return properties;
      });

      return {mainProperties, upsellProperties};
    }

    createBundleId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    async submitItems(items) {
      this.clearError();
      this.addButton.classList.add('is-loading');
      this.addButton.disabled = true;

      try {
        const response = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: JSON.stringify({items}),
        });
        const data = await response.json();

        if (!response.ok || data.status) throw data;

        this.addButton.dispatchEvent(
          new CustomEvent('theme:product:add', {
            bubbles: true,
            detail: {response: data, button: this.addButton},
          })
        );

        if (window.theme.settings.cartType === 'page' || !document.querySelector('cart-items')) {
          window.location = window.theme.routes.cart_url;
          return;
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
      } catch (error) {
        this.showError(error?.description || error?.message || this.dataset.addError);
        this.addButton.dispatchEvent(
          new CustomEvent('theme:product:add-error', {
            bubbles: true,
            detail: {response: error, button: this.addButton},
          })
        );
        this.addButton.classList.remove('is-loading');
        this.addButton.disabled = false;
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
      }
    }

    clearError() {
      const container = this.form?.querySelector(selectors.errorContainer);
      if (!container) return;
      container.replaceChildren();
      container.classList.remove('is-visible');
    }

    showError(message) {
      const container = this.form?.querySelector(selectors.errorContainer);
      if (!container) return;

      const error = document.createElement('div');
      error.className = 'errors';
      error.setAttribute('role', 'alert');
      error.textContent = message;
      container.replaceChildren(error);
      container.classList.add('is-visible');
    }
  }

  if (!customElements.get('product-metafield-upsell')) {
    customElements.define('product-metafield-upsell', ProductMetafieldUpsell);
  }
})();
